import { apiClient } from './api'
import {
  ComplianceDashboardQuery,
  ComplianceDashboardResponse,
  IdentityMatchDashboardQuery,
  IdentityMatchDashboardResponse,
  RoutingDashboardQuery,
  RoutingDashboardResponse,
  SearchDashboardQuery,
  SearchDashboardResponse,
  CaseData,
  DateFilter,
  EvaluationVersion,
  DecisionVersion,
  MeteringDashboardQuery,
  MeteringDashboardResponse,
  MeteringOffenseRow,
  TelemetryDashboardQuery,
  TelemetryDashboardResponse,
} from '@/types'

// API response wrapper type
interface ApiResponse<T> {
  success: boolean
  data: T
}

const toCsv = (values?: string[]) => (values && values.length > 0 ? values.join(',') : undefined)

export const getComplianceDashboard = async (query: ComplianceDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<ComplianceDashboardResponse>>(
    '/analytics/compliance-dashboard',
    {
      ...query,
      segment: toCsv(query.segment),
      matchStatus: toCsv(query.matchStatus),
      errorType: toCsv(query.errorType),
      mismatchCategory: toCsv(query.mismatchCategory),
      disposition: toCsv(query.disposition),
      crimeCategory: toCsv(query.crimeCategory),
      adjudication: toCsv(query.adjudication),
      courtType: toCsv(query.courtType),
    }
  )
  return response.data
}

export const getIdentityMatchDashboard = async (query: IdentityMatchDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<IdentityMatchDashboardResponse>>(
    '/analytics/identity-match',
    {
      ...query,
      matchLevel: toCsv(query.matchLevel),
      isMatch: toCsv(query.isMatch),
      subjectDataFilter: toCsv(query.subjectDataFilter),
    }
  )
  return response.data
}

export const getRoutingDashboard = async (query: RoutingDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<RoutingDashboardResponse>>('/analytics/routing', {
    ...query,
    queue: toCsv(query.queue),
    identityLevel: toCsv(query.identityLevel),
    reportability: toCsv(query.reportability),
    matchStatus: toCsv(query.matchStatus),
    idMatchValidated: toCsv(query.idMatchValidated),
    duplicate: toCsv(query.duplicate),
    ubsStatus: toCsv(query.ubsStatus),
    ubsExclusion: toCsv(query.ubsExclusion),
    aiExclusion: toCsv(query.aiExclusion),
    typeClass: toCsv(query.typeClass),
    offenseAge: toCsv(query.offenseAge),
    hasDispDate: toCsv(query.hasDispDate),
    searchType: toCsv(query.searchType),
    sourceNorm: toCsv(query.sourceNorm),
    validationStatus: toCsv(query.validationStatus),
    validationCount: query.validationCount || '',
    versionFilter: query.versionFilter || '',
    modelVersion: query.modelVersion || '',
    qaOverride: toCsv(query.qaOverride),
    includeMetrics: query.includeMetrics === false ? 'false' : 'true',
    liveApiTraffic: query.liveApiTraffic ? 'true' : undefined,
    requestSource: query.requestSource,
  })
  return response.data
}

export interface RunStats {
  reportable: number
  notReportable: number
  other: number
  automation: number
  auditor: number
  humanIntervention: number
  accurate: number
  accuracy: number
}

export interface RunComparisonResponse {
  totalRemaining: number
  offensesWithMultiRuns: number
  offensesWith3PlusRuns: number
  latestRun: RunStats
  previousRun: RunStats
  twoRunsAgo: RunStats
  changedLatestVsPrev: number
  changedPrevVsPrev2: number
}

export const getRunComparison = async (startDate: string, endDate: string, searchType?: string[], sourceNorm?: string[]) => {
  const response = await apiClient.get<ApiResponse<RunComparisonResponse>>(
    '/analytics/routing/run-comparison',
    {
      startDate,
      endDate,
      searchType: toCsv(searchType),
      sourceNorm: toCsv(sourceNorm),
    }
  )
  return response.data
}

export interface ModelVersionInfo {
  version: string
  lastDeployed: string | null
  isCurrent: boolean
  runCount: number
  lastDecisionAt: string | null
}

export interface ModelVersionsResponse {
  versions: ModelVersionInfo[]
}

export const getModelVersions = async () => {
  const response = await apiClient.get<ApiResponse<ModelVersionsResponse>>('/analytics/model-versions')
  return response.data
}

export interface VersionComparisonParams {
  startDate: string
  endDate: string
  v1?: string
  v2?: string
  mode?: 'version' | 'chronological'
  searchType?: string[]
  sourceNorm?: string[]
}

export interface VersionComparisonStats {
  totalOffenses: number
  ignoredOffenses: number
  remainingOffenses: number
  automationCount: number
  auditorCount: number
  humanInterventionCount: number
  exclusionRulesCount: number
  aiAutomatedCount: number
  researcherCount: number
  accurateCount: number
  accuracyPct: number
  automationAccuracyPct: number
  auditorAccuracyPct: number
  humanInterventionAccuracyPct: number
}

export interface VersionComparisonMetricsResponse {
  mode: 'version'
  v1: { version: string; metrics: VersionComparisonStats }
  v2: { version: string; metrics: VersionComparisonStats }
}

export type UnifiedComparisonResponse =
  | VersionComparisonMetricsResponse
  | ({ mode: 'chronological' } & RunComparisonResponse)

export const getVersionComparison = async (params: VersionComparisonParams) => {
  const response = await apiClient.get<ApiResponse<UnifiedComparisonResponse>>(
    '/analytics/routing/version-comparison',
    {
      startDate: params.startDate,
      endDate: params.endDate,
      v1: params.v1,
      v2: params.v2,
      mode: params.mode,
      searchType: toCsv(params.searchType),
      sourceNorm: toCsv(params.sourceNorm),
    },
    { timeout: 90000 }
  )
  return response.data
}

export interface RunHistoryEntry {
  runId: string
  startedAt: string | null
  completedAt: string | null
  offenseCount: number
}

export interface RunHistoryResponse {
  runs: RunHistoryEntry[]
}

export const getRunHistory = async (startDate: string, endDate: string, modelVersion: string) => {
  const response = await apiClient.get<ApiResponse<RunHistoryResponse>>(
    '/analytics/routing/run-history',
    { startDate, endDate, modelVersion }
  )
  return response.data
}

export const getJurisdictions = async () => {
  const response = await apiClient.get<ApiResponse<string[]>>('/analytics/jurisdictions')
  return response.data
}

export const getRecordCount = async (filter: DateFilter) => {
  const response = await apiClient.get<ApiResponse<{ count: number }>>('/analytics/record-count', filter)
  return response.data
}

export const exportComplianceDashboard = async (query: ComplianceDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<{ rows: any[]; totalCount: number }>>(
    '/analytics/compliance-dashboard/export',
    {
      ...query,
      segment: toCsv(query.segment),
      matchStatus: toCsv(query.matchStatus),
      errorType: toCsv(query.errorType),
      mismatchCategory: toCsv(query.mismatchCategory),
      disposition: toCsv(query.disposition),
      crimeCategory: toCsv(query.crimeCategory),
      adjudication: toCsv(query.adjudication),
      courtType: toCsv(query.courtType),
    }
  )
  return response.data
}

const EXPORT_PAGE_SIZE = 10000
const EXPORT_TIMEOUT_MS = 300000

export const exportRoutingDashboard = async (
  query: RoutingDashboardQuery,
  options?: { timeout?: number },
) => {
  const response = await apiClient.get<
    ApiResponse<{ rows: any[]; totalCount: number; page?: number; pageSize?: number }>
  >(
    '/analytics/routing/export',
    {
      ...query,
      queue: toCsv(query.queue),
      identityLevel: toCsv(query.identityLevel),
      reportability: toCsv(query.reportability),
      matchStatus: toCsv(query.matchStatus),
      idMatchValidated: toCsv(query.idMatchValidated),
      duplicate: toCsv(query.duplicate),
      ubsStatus: toCsv(query.ubsStatus),
      ubsExclusion: toCsv(query.ubsExclusion),
      aiExclusion: toCsv(query.aiExclusion),
      typeClass: toCsv(query.typeClass),
      offenseAge: toCsv(query.offenseAge),
      hasDispDate: toCsv(query.hasDispDate),
      searchType: toCsv(query.searchType),
      sourceNorm: toCsv(query.sourceNorm),
      validationStatus: toCsv(query.validationStatus),
      validationCount: query.validationCount || '',
      versionFilter: query.versionFilter || '',
      modelVersion: query.modelVersion || '',
      qaOverride: toCsv(query.qaOverride),
    },
    { timeout: options?.timeout ?? EXPORT_TIMEOUT_MS },
  )
  return response.data
}

/** Fetches every matching routing row by paginating the export endpoint (no overall row cap). */
export const exportAllRoutingDashboardRows = async (
  query: Omit<RoutingDashboardQuery, 'page' | 'pageSize'>,
  onProgress?: (fetched: number, total: number) => void,
) => {
  const pageSize = EXPORT_PAGE_SIZE
  let page = 1
  const allRows: any[] = []
  let totalCount = Number.POSITIVE_INFINITY

  while (allRows.length < totalCount) {
    const data = await exportRoutingDashboard({
      ...query,
      page,
      pageSize,
    })
    if (!data) {
      throw new Error('Export returned an empty response from the API')
    }
    const rows = data.rows || []
    const reportedTotal = Number(data.totalCount)
    if (Number.isFinite(reportedTotal) && reportedTotal > 0) {
      totalCount = reportedTotal
    }
    allRows.push(...rows)
    onProgress?.(allRows.length, totalCount)

    if (rows.length === 0 || rows.length < pageSize || allRows.length >= totalCount) {
      break
    }
    page += 1
  }

  return { rows: allRows, totalCount: Number.isFinite(totalCount) ? totalCount : allRows.length }
}

export const updateQaDecision = async (offenseId: string, searchId: string, qaDecision: string) => {
  const response = await apiClient.patch<ApiResponse<void>>(
    '/analytics/routing/qa-decision',
    { offenseId, searchId, qaDecision }
  )
  return response
}

export const getIdentityMatchSSN = async (matchId: string) => {
  const response = await apiClient.get<ApiResponse<{ candidate_ssn: string | null; subject_ssn: string | null }>>(
    `/analytics/identity-match/ssn/${matchId}`
  )
  return response.data
}

// Fetch all offenses for a given search_id from the database
export const getOffensesBySearchId = async (searchId: string, modelVersion?: string) => {
  const params: Record<string, string> = {}
  if (modelVersion) params.modelVersion = modelVersion
  const response = await apiClient.get<ApiResponse<{ rows: any[]; totalCount: number }>>(
    `/analytics/offenses-by-search/${searchId}`,
    { params },
  )
  return response.data
}

// Fetch offenses with routing data for Compare Searches page
export const getOffensesWithRouting = async (searchId: string, modelVersion?: string) => {
  const params: Record<string, string> = {}
  if (modelVersion) params.modelVersion = modelVersion
  const response = await apiClient.get<ApiResponse<{ rows: any[]; totalCount: number }>>(
    `/analytics/compare-offenses/${searchId}`,
    { params },
  )
  return response.data
}

// Fetch bronze (source) data for a given search_id
export const getBronzeResponse = async (searchId: string) => {
  const response = await apiClient.get<ApiResponse<any>>(
    `/analytics/routing/bronze/${searchId}`
  )
  return response.data
}

// Business metrics response type
export interface BusinessMetricsResponse {
  volume: {
    totalSearches: number
    totalOffenses: number
    totalDays: number
    weekdaySearches: number
    weekendSearches: number
    weekdayCount: number
    weekendCount: number
    weekdayAvg: number
    weekendAvg: number
  }
  fulfillment: {
    totalDecisions: number
    fulfilledCount: number
    groundTruthCount: number
    matchedCount: number
    fulfillmentRate: number
    accuracyRate: number
  }
}

// Fetch business metrics for SNH AI dashboard
export const getBusinessMetrics = async (startDate: string, endDate: string) => {
  const response = await apiClient.get<ApiResponse<BusinessMetricsResponse>>(
    '/analytics/business-metrics',
    { startDate, endDate }
  )
  return response.data
}

// QA Error Rate types
export interface QaErrorRateResponse {
  metrics: {
    totalComparisons: number
    decisionsChanged: number
    errorRate: number
    reportableToNotReportable: number
    notReportableToReportable: number
    avgTimeToPickupHours: number | null
    avgTimeToQaHours: number | null
    avgProcessingTurnaroundHours: number | null
    uniqueCases: number
    periodDays: number
  }
  trend: Array<{
    date: string
    total: number
    changed: number
    rate: number
  }>
  topCases: Array<{
    searchId: string
    mismatchCount: number
    totalCharges: number
    courtSearchId: string | null
  }>
  rows: Array<{
    searchId: string
    offenseId: string
    preDoNotReport: boolean | null
    preResult: string | null
    preReportability: string
    postDoNotReport: boolean | null
    postResult: string | null
    postReportability: string
    decisionChanged: boolean
    receivedDate: string | null
    pickupDate: string | null
    finalQaDate: string | null
    searchedDate: string | null
    timeToPickupHours: number | null
    timeToQaHours: number | null
    processingTurnaroundHours: number | null
    comparedAt: string
    courtSearchId: string | null
    preNotes: string | null
    postNotes: string | null
    charge: string | null
    disposition: string | null
    chargeDate: string | null
    caseNumber: string | null
    jurisdiction: string | null
    jurisdictionState: string | null
  }>
  totalMismatchRows: number
}

export const getQaErrorRate = async (days = 30, page = 1, pageSize = 50) => {
  const response = await apiClient.get<ApiResponse<QaErrorRateResponse>>(
    '/analytics/qa-error-rate',
    { days, page, pageSize }
  )
  return response.data
}

export const getSearchDashboard = async (query: SearchDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<SearchDashboardResponse>>(
    '/analytics/search-dashboard',
    {
      ...query,
      searchType: toCsv(query.searchType),
      includeMetrics: query.includeMetrics !== false ? 'true' : 'false',
    }
  )
  return response.data
}

export const exportSearchDashboard = async (query: SearchDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<SearchDashboardResponse>>(
    '/analytics/search-dashboard/export',
    {
      ...query,
      searchType: toCsv(query.searchType),
      includeMetrics: 'false',
    }
  )
  return response.data
}

export const getCasesBySearchId = async (searchId: string) => {
  const response = await apiClient.get<ApiResponse<CaseData[]>>(
    `/analytics/cases-by-search/${searchId}`
  )
  return response.data
}

export const getEvaluationsBySearch = async (searchId: string) => {
  const response = await apiClient.get<ApiResponse<EvaluationVersion[]>>(
    `/analytics/evaluations-by-search/${searchId}`,
    undefined,
    { timeout: 60000 },
  )
  return response.data
}

export const getDecisionHistory = async (searchId: string) => {
  const response = await apiClient.get<ApiResponse<DecisionVersion[]>>(
    `/analytics/decision-history/${searchId}`,
    undefined,
    { timeout: 60000 },
  )
  return response.data
}

export interface ClientOption {
  tenant_id: string
  tenant_name: string | null
  search_count: number
}

export const getClients = async (): Promise<ClientOption[]> => {
  const response = await apiClient.get<ApiResponse<ClientOption[]>>('/analytics/clients')
  return response.data
}

export const getMeteringDashboard = async (query: MeteringDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<MeteringDashboardResponse>>(
    '/analytics/metering',
    query
  )
  return response.data
}

export const getMeteringOffenses = async (searchId: string, modelVersion?: string) => {
  const response = await apiClient.get<ApiResponse<MeteringOffenseRow[]>>(
    `/analytics/metering/offenses/${encodeURIComponent(searchId)}`,
    modelVersion ? { modelVersion } : undefined,
  )
  return response.data
}

export const getTelemetryDashboard = async (query: TelemetryDashboardQuery) => {
  const response = await apiClient.get<ApiResponse<TelemetryDashboardResponse>>(
    '/analytics/telemetry',
    query
  )
  return response.data
}
